import { format } from 'date-fns';
import { Typography } from '@components/Typography';
import ResponseItem from '@components/ResponseItem';
import { I18N_DATE_FORMAT } from '@helpers/dates';
import { getSplittedName } from '@helpers/userName';
import { getIntegrationIcon } from '@helpers/integrations';
import { useTranslation } from '@hooks';
import { ProfileType } from '@constants/integrations';
import Reply from './Reply';
import AssignContact from './AssignContact';
import { Keyword, KeywordsContainer, ReplyConnection, IconWrapper, FooterWrapper } from './styles';

const FeedbackBody = ({ item, language, isMobile }) => {
    const { t } = useTranslation();
    return (
        <>
            <div
                style={{
                    display: 'flex',
                    position: 'relative',
                    flexDirection: 'column',
                    maxWidth: 600,
                }}
            >
                <Typography
                    variant="body2"
                    sx={{
                        whiteSpace: 'pre-line',
                        marginLeft: '48px',
                        marginTop: isMobile && '8px',
                        ...(!item?.text && { color: 'rgba(0, 0, 0, 0.38)' }),
                        '@media (max-width: 1024px)': {
                            marginLeft: 0,
                        },
                    }}
                >
                    {item?.text || t('ReviewsManage.onlyRating')}
                </Typography>
                {!isMobile && (item?.ownerAnswer !== null || item?.isDeleted) && <ReplyConnection />}
            </div>
            <Reply language={language} review={item} isMobile={isMobile} />
        </>
    );
};

const InfoCaption = ({ date, companyName, profileName }) => {
    const { t } = useTranslation();
    return (
        <span>
            {t('ReviewsManage.viaOn', { integration: ProfileType[profileName], date })}
            {' - '}
            {companyName}
        </span>
    );
};

const IntegrationLabel = ({ link, profileName }) => {
    const { t } = useTranslation();

    return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
            {link && (
                <a
                    href={link}
                    target="_blank"
                    rel="noreferrer noopener"
                    style={{ textDecorationColor: '#1F4C5C', lineHeight: '14px' }}
                >
                    <Typography variant="caption" sx={{ marginRight: '18px', color: '#1F4C5C' }}>
                        {t('ReviewsManage.viewOn', {
                            integration: ProfileType[profileName],
                        })}
                    </Typography>
                </a>
            )}
            <IconWrapper>{getIntegrationIcon(profileName)}</IconWrapper>
        </div>
    );
};

const ReviewItem = ({ item, index, language, isMobile, companies, onClickContactDetails }) => {
    const { t } = useTranslation();

    const date = item?.updatedAt
        ? format(new Date(item?.updatedAt), 'dd MMM yyyy', { locale: I18N_DATE_FORMAT[language] })
        : t('common.noData');

    const companyName = companies?.find(company => company.companyId === item?.locationId)?.internalName;
    const { firstName, lastName } = getSplittedName(item?.userName);

    return (
        <ResponseItem
            key={item?.id || `feedback-${index}`}
            index={index}
            firstName={firstName}
            lastName={lastName}
            viewed={item?.viewed}
            rating={item?.rating}
            userProfileImg={item?.userPhotoUrl}
            infoCaption={
                <InfoCaption
                    date={date}
                    companyName={companyName || t('common.noData')}
                    profileName={item?.integrationName}
                />
            }
            body={<FeedbackBody item={item} language={language} isMobile={isMobile} />}
            footer={
                <FooterWrapper hasKeywords={!!item?.aspects?.length}>
                    {!!item?.aspects?.length && (
                        <KeywordsContainer>
                            {item?.aspects?.map((a, index) => (
                                <Keyword key={index} variant="caption" type={a.sentiment}>
                                    {a.aspect}
                                </Keyword>
                            ))}
                        </KeywordsContainer>
                    )}
                    <AssignContact
                        contactId={item?.contactId}
                        contactName={item?.contactName}
                        reviewId={item?.reviewId}
                        contactPhoneNumber={item?.contactPhoneNumber}
                        contactEmail={item?.contactEmail}
                        contactExternalId={item?.contactExternalId}
                        onClickContactDetails={onClickContactDetails}
                    />
                </FooterWrapper>
            }
            footerStyles={{ justifyContent: 'flex-start' }}
            integrationLabel={<IntegrationLabel link={item?.link} profileName={item.integrationName} />}
        />
    );
};

ReviewItem.displayName = 'ReviewItem';

export default ReviewItem;
